import type { TNode } from '../Node';
import type { SelectionDescendantMap } from '../useTreeApi';

const insertDescendantSelections = ({
  selectionDescendantMap,
  ancestors,
}: {
  selectionDescendantMap: SelectionDescendantMap;
  ancestors: string[];
}): SelectionDescendantMap => {
  const [id, ...rest] = ancestors;
  if (id === undefined) return selectionDescendantMap;

  const child: SelectionDescendantMap =
    selectionDescendantMap.get(id) ?? new Map();

  return selectionDescendantMap.set(
    id,
    insertDescendantSelections({
      selectionDescendantMap: child,
      ancestors: rest,
    })
  );
};

const removeDescendantSelections = ({
  selectionDescendantMap,
  ancestors,
  selectedIds,
}: {
  selectionDescendantMap: SelectionDescendantMap;
  ancestors: string[];
  selectedIds: string[];
}): SelectionDescendantMap => {
  const [id, ...rest] = ancestors;
  if (id === undefined) return selectionDescendantMap;

  if (rest.length === 0) {
    const noOfChildren = selectionDescendantMap.get(id)?.size || 0;
    if (noOfChildren > 0) return selectionDescendantMap;
    else {
      selectionDescendantMap.delete(id);
      return selectionDescendantMap;
    }
  } else {
    const newSelectionMap = removeDescendantSelections({
      selectionDescendantMap: selectionDescendantMap.get(id) || new Map(),
      ancestors: rest,
      selectedIds,
    });
    if (newSelectionMap.size === 0 && !selectedIds.includes(id)) {
      selectionDescendantMap.delete(id);
      return selectionDescendantMap;
    } else return selectionDescendantMap.set(id, newSelectionMap);
  }
};

// One of node or ancestors must be provided
type UpdateDescendantSelectionsBaseProps =
  | {
      node: TNode;
      selectionDescendantMap: SelectionDescendantMap;
      ancestors?: string[];
    }
  | {
      node?: undefined;
      selectionDescendantMap: SelectionDescendantMap;
      ancestors: string[];
    };

type InsertDescendantSelectionsProps = UpdateDescendantSelectionsBaseProps & {
  operation: 'INSERT';
};

type RemoveDescendantSelectionsProps = UpdateDescendantSelectionsBaseProps & {
  operation: 'REMOVE';
  selectedIds: string[];
};

type UpdateDescendantSelectionsProps =
  | InsertDescendantSelectionsProps
  | RemoveDescendantSelectionsProps;

export const updateDescendantSelections = (
  props: UpdateDescendantSelectionsProps
): string[] => {
  const { operation, node, selectionDescendantMap } = props;

  // If node doesn't exist, props.ancestors *must* exist (enforced by type)
  const ancestors = node ? getAncestors(node) : props.ancestors;

  switch (operation) {
    case 'INSERT':
      return getIndeterminateParents(
        insertDescendantSelections({ selectionDescendantMap, ancestors })
      );
    case 'REMOVE':
      return getIndeterminateParents(
        removeDescendantSelections({
          selectionDescendantMap,
          ancestors,
          selectedIds: props.selectedIds,
        })
      );
  }
};

const getAncestors = (node: TNode): string[] => {
  if (node.parent) return [...getAncestors(node.parent), node.id];
  return [];
};

const getIndeterminateParents = (
  selectionDescendantMap: SelectionDescendantMap
): string[] => {
  const indeterminateParents: string[] = [];
  selectionDescendantMap.forEach((value, key) => {
    if (value.size === 0) return;
    else {
      indeterminateParents.push(key);
      indeterminateParents.push(...getIndeterminateParents(value));
    }
  });
  return indeterminateParents;
};
